import { ArrowUpRight } from "lucide-react";
import {
  AMAZON_URL,
  COVER_URL,
  ISBN,
  ASIN_PAPERBACK,
  ASIN_HARDCOVER,
  URL_PAPERBACK,
  URL_HARDCOVER,
} from "@/constants/site";
import { FadeUp } from "./Reveal";

const DETAILS = [
  { label: "Series", value: "The Aurora Chronicles — Book 1 of 2" },
  { label: "Published", value: "Sept 30, 2025" },
  { label: "Print length", value: "125 pages" },
  { label: "Reading age", value: "14 – 18 years" },
  { label: "Language", value: "English" },
  { label: "Publisher", value: "Independently published" },
  { label: "ISBN-13", value: ISBN },
  { label: "Formats", value: "Paperback $24.99 · Hardcover $41.19" },
  { label: "Paperback ASIN", value: ASIN_PAPERBACK, href: URL_PAPERBACK },
  { label: "Hardcover ASIN", value: ASIN_HARDCOVER, href: URL_HARDCOVER },
];

export const Showcase = () => (
  <section
    id="book"
    data-testid="book-section"
    className="relative py-28 md:py-36 bg-[#0a0a0a] border-y border-white/10 overflow-hidden"
  >
    <div
      className="absolute top-0 left-1/2 -translate-x-1/2 w-[900px] h-[420px] pointer-events-none"
      style={{ background: "radial-gradient(ellipse at top, rgba(16,185,129,0.12), transparent 65%)" }}
      aria-hidden="true"
    />
    <div className="relative max-w-[1400px] mx-auto px-6 lg:px-12 grid lg:grid-cols-12 gap-16 items-center">
      {/* Cover — spotlight, clipped frame */}
      <FadeUp className="lg:col-span-5 flex justify-center">
        <div className="relative" data-testid="book-cover">
          <div
            className="absolute -inset-20 pointer-events-none"
            style={{
              background:
                "radial-gradient(ellipse at 50% 30%, rgba(56,189,248,0.18), rgba(16,185,129,0.10) 45%, transparent 72%)",
              filter: "blur(28px)",
            }}
            aria-hidden="true"
          />
          <div className="relative clip-frame border border-white/10 bg-[#050505] shadow-[0_50px_140px_-30px_rgba(56,189,248,0.35)]">
            <img
              src={COVER_URL}
              alt="Northern Lights Rising — The Aurora Chronicles, Book One, front cover"
              className="w-64 sm:w-80 lg:w-[420px] block"
              loading="lazy"
            />
            <div className="absolute inset-0 pointer-events-none bg-gradient-to-b from-white/10 via-transparent to-black/30" />
          </div>
        </div>
      </FadeUp>

      {/* Details */}
      <div className="lg:col-span-7">
        <FadeUp>
          <span className="font-tech text-[11px] uppercase tracking-[0.3em] text-emerald-400">
            Book One
          </span>
          <h2 className="font-display font-light tracking-tighter leading-[1.02] text-[clamp(2.2rem,4.6vw,4.5rem)] mt-8">
            Northern Lights <span className="italic text-zinc-400">Rising</span>
          </h2>
        </FadeUp>

        <FadeUp delay={0.1}>
          <dl className="mt-14 grid sm:grid-cols-2 gap-x-12">
            {DETAILS.map(({ label, value, href }) => (
              <div
                key={label}
                className="flex flex-col gap-2 border-t border-white/10 py-5"
                data-testid={`book-detail-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              >
                <dt className="font-tech text-[10px] uppercase tracking-[0.3em] text-zinc-500">
                  {label}
                </dt>
                <dd className="text-zinc-200 font-light text-sm md:text-base">
                  {href ? (
                    <a
                      href={href}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`book-detail-link-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                      className="hover:text-emerald-300 transition-colors duration-300"
                    >
                      {value}
                    </a>
                  ) : (
                    value
                  )}
                </dd>
              </div>
            ))}
          </dl>
        </FadeUp>

        <FadeUp delay={0.15}>
          <a
            href={AMAZON_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="book-buy-button"
            className="group mt-12 inline-flex items-center gap-3 bg-emerald-500 text-black font-tech text-xs uppercase tracking-[0.25em] px-8 py-4 hover:bg-emerald-400 hover:-translate-y-0.5 transition-[background-color,transform] duration-300"
          >
            Buy on Amazon
            <ArrowUpRight
              size={16}
              className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
            />
          </a>
        </FadeUp>
      </div>
    </div>
  </section>
);

export default Showcase;
